import React, { useState, useRef, useEffect } from 'react';

export interface ProductOption {
  sku: string;
  name: string;
  totalUnits: number;
}

interface Props {
  products: ProductOption[];
  selected: string[];
  onChange: (skus: string[]) => void;
  colors: string[];
}

const MAX_SELECTED = 5;

export function ProductFilter({ products, selected, onChange, colors }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (products.length === 0) { return null; }

  const filtered = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  function toggle(sku: string) {
    if (selected.includes(sku)) {
      onChange(selected.filter(s => s !== sku));
    } else if (selected.length < MAX_SELECTED) {
      onChange([...selected, sku]);
    }
  }

  function remove(sku: string) {
    onChange(selected.filter(s => s !== sku));
  }

  const selectedProducts = selected
    .map((sku, i) => ({ sku, name: products.find(p => p.sku === sku)?.name ?? sku, color: colors[i % colors.length] }))
    .filter(p => p.name);

  const atMax = selected.length >= MAX_SELECTED;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Label */}
      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>
        Compare products
      </div>

      {/* Trigger button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0.5rem 0.75rem',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          backgroundColor: '#fff',
          cursor: 'pointer',
          fontSize: '0.85rem',
          color: selected.length === 0 ? '#9ca3af' : '#374151',
          gap: '0.5rem',
        }}
      >
        <span>
          {selected.length === 0
            ? 'Select up to 5 products…'
            : `${selected.length} of ${MAX_SELECTED} selected`}
        </span>
        <span style={{ fontSize: '0.7rem', color: '#9ca3af', flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {/* Selected chips */}
      {selectedProducts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.5rem' }}>
          {selectedProducts.map(p => (
            <span
              key={p.sku}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.2rem 0.55rem',
                borderRadius: '999px',
                backgroundColor: p.color + '18',
                border: `1px solid ${p.color}55`,
                fontSize: '0.78rem',
                color: '#374151',
                maxWidth: '200px',
              }}
            >
              <span
                style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color, flexShrink: 0 }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}
              </span>
              <button
                onClick={() => remove(p.sku)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9ca3af', lineHeight: 1, fontSize: '0.85rem', flexShrink: 0 }}
                aria-label={`Remove ${p.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown panel */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 200,
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: '280px',
            maxHeight: '340px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Search */}
          <div style={{ padding: '0.6rem 0.75rem', borderBottom: '1px solid #f3f4f6' }}>
            <input
              autoFocus
              type="text"
              placeholder="Search products…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '0.4rem 0.6rem',
                fontSize: '0.85rem',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Status bar */}
          <div style={{ padding: '0.3rem 0.75rem', fontSize: '0.72rem', color: atMax ? '#dc2626' : '#9ca3af', borderBottom: '1px solid #f9fafb' }}>
            {atMax ? `Max ${MAX_SELECTED} selected — remove one to add another` : `${selected.length}/${MAX_SELECTED} selected`}
          </div>

          {/* Options list */}
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                No products match
              </div>
            ) : (
              filtered.map((p, i) => {
                const isSelected = selected.includes(p.sku);
                const selIdx = selected.indexOf(p.sku);
                const color = selIdx >= 0 ? colors[selIdx % colors.length] : colors[i % colors.length];
                const disabled = !isSelected && atMax;

                return (
                  <div
                    key={p.sku}
                    onClick={() => !disabled && toggle(p.sku)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.6rem',
                      padding: '0.5rem 0.75rem',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? color + '0d' : 'transparent',
                      opacity: disabled ? 0.4 : 1,
                      transition: 'background-color 0.1s',
                    }}
                    onMouseEnter={e => { if (!disabled && !isSelected) { (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f9fafb'; } }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.backgroundColor = isSelected ? color + '0d' : 'transparent'; }}
                  >
                    {/* Checkbox */}
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: '4px',
                        border: isSelected ? `2px solid ${color}` : '2px solid #d1d5db',
                        backgroundColor: isSelected ? color : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.1s',
                      }}
                    >
                      {isSelected && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                          <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>

                    {/* Name + volume */}
                    <span style={{ flex: 1, fontSize: '0.85rem', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af', flexShrink: 0 }}>
                      {p.totalUnits.toLocaleString()}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
